import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Tooltip,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  ListItemIcon,
  InputBase,
  Button,
  Chip
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import Settings from '@mui/icons-material/Settings';
import Logout from '@mui/icons-material/Logout';
import Person from '@mui/icons-material/Person';
import Group from '@mui/icons-material/Group';
import ShieldIcon from '@mui/icons-material/Shield';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import SearchIcon from '@mui/icons-material/Search';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import TourIcon from '@mui/icons-material/Tour';
import CreateApplication from '../../pages/developer/CreateApplication';
import { useDispatch, useSelector } from 'react-redux';
import { clearToken, clearUserDetails } from '../../features/auth/authSlice';
import { httpService } from '../../services/httpService';
import { API_BASE_URL, REQ_ENC_KEY } from '../../services/api';
import { setAppId, setAppName, setAppStatus, setRegion, toggleChatsEnabled, toggleChatBotEnabled, toggleCallsEnabled, clearApplicationDetails, toggleScheduledCallsEnabled, toggleNotificationsEnabled, toggleExportDataAccess, toggleChatAnalyticsEnabled, toggleCallAnalyticsEnabled, toggleSentimentAnalysisEnabled, toggleUserAnalyticsEnabled, setPrimaryColor, setSecondaryColor } from '../../features/application/applicationDetailsSlice';
import { decryptResponse, clearSessionKey, getSessionKey, encryptResponse } from '../../services';
import { hasRouteAccess } from '../../routes/rolePermissions';
import { useTour } from '../../context/TourContext';

interface User {
  name: string;
  email: string;
}

interface ApplicationsResponse {
  status: number;
  data: {
    applications: Array<{
      organization_id: string;
      app_id: string;
      app_name: string;
      app_status: string;
      region: string;
      is_chats_enabled: boolean;
      is_calls_enabled: boolean;
      is_scheduled_calls_enabled: boolean;
      is_notifications_enabled: boolean;
      allow_data_access: boolean;
      access_chat_analytics: boolean;
      access_call_analytics: boolean;
      access_sentiment_analysis: boolean;
      access_user_analytics: boolean;
      primary_color: string;
      secondary_color: string;
    }>;
  };
  message?: string;
  error?: any;
}

export default function Header(): React.JSX.Element {
  const dispatch = useDispatch();
  const userDetails = useSelector((state: any) => state.auth.userDetails);
  const applicationId = useSelector((state: any) => state.applicationDetails.app_id);
  const applicationDataState = useSelector((state: any) => state.applicationDetails.is_fetch_data);
  const { startTour, isTourOpen, currentStepData } = useTour();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open: boolean = Boolean(anchorEl);
  const [dropdownAnchor, setDropdownAnchor] = useState<null | HTMLElement>(null);
  const dropdownOpen: boolean = Boolean(dropdownAnchor);
  const [createDialogOpen, setCreateDialogOpen] = useState<boolean>(false);
  const [applications, setApplications] = useState<any[]>([]);
  const navigate = useNavigate();
  const location = useLocation();

  const isTourTarget = (targetId: string): boolean =>
    Boolean(isTourOpen && currentStepData?.target === targetId);

  useEffect(() => {
    const fetchData = async () => {
      const payload = {
        request_id: "notegtest1",
        timestamp: new Date().toISOString(),
        action: "get_all_applications",
        data: await encryptResponse({
          organization_id: userDetails?.organization_id || '',
          user_id: userDetails?.user_id || '',
        }, REQ_ENC_KEY)
      };

      try {
        const response = await httpService.post<any>(`${API_BASE_URL}/user/getAllApplications`, payload);
        if (response.data.status === 200 && response.data.data) {
          const data = response.data.data;
          const decrypted = await decryptResponse<{
            applications: any[];
          }>(data, getSessionKey() as string);
          const { applications } = decrypted;
          const fetchedApplications = applications || [];
          setApplications(fetchedApplications);
          if (fetchedApplications.length > 0) {
            const appIdFromRedux = applicationId;
            const filteredApplications = appIdFromRedux
              ? fetchedApplications.filter(app => app.app_id === appIdFromRedux)
              : fetchedApplications;

            const firstApp = filteredApplications[0];
            if (firstApp) {
              setSelectedApp(firstApp.app_name);
              dispatch(setAppId(firstApp.app_id));
              dispatch(setAppName(firstApp.app_name));
              dispatch(setAppStatus(firstApp.app_status));
              dispatch(setRegion(firstApp.region));
              dispatch(toggleChatsEnabled(firstApp.is_chats_enabled));
              dispatch(toggleChatBotEnabled(firstApp.is_chat_bot_enabled));
              dispatch(toggleCallsEnabled(firstApp.is_calls_enabled));
              dispatch(toggleScheduledCallsEnabled(firstApp.is_scheduled_calls_enabled));
              dispatch(toggleNotificationsEnabled(firstApp.is_notifications_enabled));
              dispatch(toggleExportDataAccess(firstApp.allow_data_access));
              dispatch(toggleChatAnalyticsEnabled(firstApp.access_chat_analytics));
              dispatch(toggleCallAnalyticsEnabled(firstApp.access_call_analytics));
              dispatch(toggleSentimentAnalysisEnabled(firstApp.access_sentiment_analysis));
              dispatch(toggleUserAnalyticsEnabled(firstApp.access_user_analytics));
              dispatch(setPrimaryColor(firstApp.primary_color || '#1976d2'));
              dispatch(setSecondaryColor(firstApp.secondary_color || '#dc004e'));
            }
          }
        } else {
          setApplications([]);
        }
      } catch (error) {
        setApplications([]);
      }
    };
    if (!createDialogOpen) {
      fetchData();
    }
  }, [createDialogOpen, applicationDataState, applicationId]);

  const [selectedApp, setSelectedApp] = useState<string>(applications[0]?.app_name || '');

  const handleOpen = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = (): void => {
    setAnchorEl(null);
  };

  const handleDropdownClick = (event: React.MouseEvent<HTMLElement>): void => {
    setDropdownAnchor(event.currentTarget);
  };

  const handleDropdownClose = (): void => {
    setDropdownAnchor(null);
  };

  const handleOpenCreateDialog = (): void => {
    setCreateDialogOpen(true);
    handleDropdownClose();
  };

  const handleCloseCreateDialog = (): void => {
    setCreateDialogOpen(false);
  };

  const handleLogout = async (): Promise<void> => {
    // Destroy the server-side session and clear the httpOnly session cookie.
    // Best-effort: even if the call fails, we still clear local auth state.
    try {
      await httpService.post(`${API_BASE_URL}/user/logoutAdminSession`, {});
    } catch (error) {
      console.error('Logout request failed:', error);
    }
    clearSessionKey();
    dispatch(clearApplicationDetails());
    dispatch(clearToken());
    dispatch(clearUserDetails());
    localStorage.removeItem('sampark_tour_completed')
    navigate('/login');
  };

  const handleAppSelect = (appName: string): void => {
    const selectedApplication = applications.find((app) => app.app_name === appName);
    if (selectedApplication) {
      setSelectedApp(appName);
      setDropdownAnchor(null);
      dispatch(setAppId(selectedApplication.app_id));
      dispatch(setAppName(selectedApplication.app_name));
      dispatch(setAppStatus(selectedApplication.app_status));
      dispatch(setRegion(selectedApplication.region));
      dispatch(toggleChatsEnabled(selectedApplication.is_chats_enabled));
      dispatch(toggleChatBotEnabled(selectedApplication.is_chat_bot_enabled));
      dispatch(toggleCallsEnabled(selectedApplication.is_calls_enabled));
      dispatch(toggleScheduledCallsEnabled(selectedApplication.is_scheduled_calls_enabled));
      dispatch(toggleNotificationsEnabled(selectedApplication.is_notifications_enabled));
      dispatch(toggleExportDataAccess(selectedApplication.allow_data_access));
      dispatch(toggleChatAnalyticsEnabled(selectedApplication.access_chat_analytics));
      dispatch(toggleCallAnalyticsEnabled(selectedApplication.access_call_analytics));
      dispatch(toggleSentimentAnalysisEnabled(selectedApplication.access_sentiment_analysis));
      dispatch(toggleUserAnalyticsEnabled(selectedApplication.access_user_analytics));
      dispatch(setPrimaryColor(selectedApplication.primary_color || '#1976d2'));
      dispatch(setSecondaryColor(selectedApplication.secondary_color || '#dc004e'));
    }
  };

  const user: User = {
    name: userDetails?.user_full_name || 'Guest User',
    email: userDetails?.email_id || 'guest@example.com',
  };

  const activePlanDetails = userDetails?.activePlanDetails;
  const planInfo = userDetails?.plan_details;
  const activePlanName = activePlanDetails?.plan_name || planInfo?.plan_name || 'Free Plan';

  const handlePlanClick = (): void => {
    navigate('/plans-and-billing');
  };

  const handleQuickTourClick = (): void => {
    startTour();
  };

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'white',
          color: 'black',
        }}
      >
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <img
              src="/images/sampark-logo.png"
              alt="Sampark Logo"
              style={{ height: '60px', width: 'auto' }}
            />
          </Box>
          <Box
            ml={2}
            id="tour-app-selector"
            sx={isTourTarget('tour-app-selector') ? {
              outline: '2px solid #ff9800',
              outlineOffset: 4,
              borderRadius: 1,
              position: 'relative',
              zIndex: (theme) => theme.zIndex.modal + 12,
            } : undefined}
          >
            <Tooltip title="Select Application">
              <span>
                <Button
                  onClick={handleDropdownClick}
                  endIcon={<ArrowDropDownIcon />}
                  disabled={location.pathname === '/dashboard' || location.pathname === '/sentiment-aggregate-analytics' || location.pathname === '/sentiment-participant-analytics' || location.pathname === '/sentiment-call-analytics'}
                  sx={{
                    textTransform: 'none',
                    color: 'black',
                    fontWeight: 600,
                  }}
                >
                  {selectedApp || 'Select Application'}
                </Button>
              </span>
            </Tooltip>
            <Menu
              anchorEl={dropdownAnchor}
              open={dropdownOpen}
              onClose={handleDropdownClose}
              slotProps={{
                paper: { sx: { width: 300, mt: 1 } }
              }}
            >
              <Box sx={{ px: 2, pt: 1.5, display: 'flex', alignItems: 'center' }}>
                <InputBase
                  placeholder="Search"
                  sx={{
                    flex: 1,
                    border: '1px solid #ccc',
                    px: 1,
                    borderRadius: 1,
                    height: 36,
                    fontSize: 14,
                  }}
                />
                <IconButton size="small">
                  <SearchIcon fontSize="small" />
                </IconButton>
              </Box>
              <Box sx={{ px: 2, py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>Recently created</Typography>
                <ArrowDropDownIcon fontSize="small" />
              </Box>
              <Divider />
              {applications.map((item) => (
                <MenuItem key={`${item.organization_id}-${item.app_id}`} onClick={() => handleAppSelect(item.app_name)}>
                  <Box>
                    <Typography fontWeight={500}>{item.app_name}</Typography>
                    <Typography variant="body2" color="primary.main">{item.region}</Typography>
                  </Box>
                </MenuItem>
              ))}
              <Divider />
              <MenuItem onClick={handleOpenCreateDialog}>
                <AddCircleOutlineIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography sx={{ color: 'primary.main', fontWeight: 600 }}>Create application</Typography>
              </MenuItem>
            </Menu>
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          {/* Quick Tour */}
          <Box sx={{ mr: 1.5 }}>
            <Tooltip title="Take a quick tour of the dashboard">
              <Button
                variant="outlined"
                size="small"
                startIcon={<TourIcon />}
                onClick={handleQuickTourClick}
                disabled={isTourOpen}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderColor: '#1976d2',
                  color: '#1976d2',
                  '&:hover': {
                    borderColor: '#1565c0',
                    backgroundColor: '#f0f7ff',
                  },
                }}
              >
                Quick Tour
              </Button>
            </Tooltip>
          </Box>

          {/* Active Plan Chip */}
          <Box
            sx={{ mr: 2 }}
            id="tour-active-plan"
            style={isTourTarget('tour-active-plan') ? {
              outline: '2px solid #ff9800',
              outlineOffset: 4,
              borderRadius: 8,
              position: 'relative',
              zIndex: 1400,
            } : undefined}
          >
            <Tooltip title="View Plans & Billing">
              <Chip
                icon={<WorkspacePremiumIcon />}
                label={activePlanName}
                onClick={handlePlanClick}
                clickable
                sx={{
                  backgroundColor: '#f0f7ff',
                  color: '#1976d2',
                  fontWeight: 600,
                  border: '1px solid #1976d2',
                  '&:hover': {
                    backgroundColor: '#1976d2',
                    color: 'white',
                    '& .MuiChip-icon': {
                      color: 'white',
                    },
                  },
                  transition: 'all 0.3s ease',
                  '& .MuiChip-icon': {
                    color: '#1976d2',
                  },
                }}
              />
            </Tooltip>
          </Box>

          <Box
            ml={2}
            id="tour-account-menu"
            sx={isTourTarget('tour-account-menu') ? {
              outline: '2px solid #ff9800',
              outlineOffset: 4,
              borderRadius: '50%',
              position: 'relative',
              zIndex: (theme) => theme.zIndex.modal + 12,
            } : undefined}
          >
            <Tooltip title="Account settings">
              <IconButton onClick={handleOpen} size="small" sx={{ ml: 2 }}>
                <Avatar sx={{ width: 32, height: 32 }}>{user.name[0]}</Avatar>
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              onClick={handleClose}
              slotProps={{
                paper: {
                  elevation: 4,
                  sx: {
                    mt: 1.5,
                    minWidth: 260,
                    '& .MuiAvatar-root': {
                      width: 32,
                      height: 32,
                      ml: -0.5,
                      mr: 1,
                    },
                  },
                },
              }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              <Box px={2} py={1}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {user.name} ({userDetails?.role})
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {user.email}
                </Typography>
              </Box>
              <Divider />
              <MenuItem onClick={() => navigate('/profile')}>
                <ListItemIcon><Person fontSize="small" /></ListItemIcon>
                Profile settings
              </MenuItem>
              {hasRouteAccess(userDetails?.role, '/members') && <MenuItem onClick={() => navigate('/members')}>
                <ListItemIcon><Group fontSize="small" /></ListItemIcon>
                Members
              </MenuItem>}
              {hasRouteAccess(userDetails?.role, '/plans-and-billing') && <MenuItem onClick={() => navigate('/plans-and-billing')}>
                <ListItemIcon><ReceiptLongIcon fontSize="small" /></ListItemIcon>
                Plans and billing
              </MenuItem>}
              <MenuItem onClick={() => navigate('/support')}>
                <ListItemIcon><SupportAgentIcon fontSize="small" /></ListItemIcon>
                Support
              </MenuItem>
              <Divider />
              <MenuItem onClick={() => handleLogout()}>
                <ListItemIcon><Logout fontSize="small" /></ListItemIcon>
                Log out
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
      {createDialogOpen && (
        <CreateApplication
          open={createDialogOpen}
          onClose={handleCloseCreateDialog}
        />
      )}
    </>
  );
}